import React, {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CameraPermissionState } from "../../types/Modals/ErrorModal";
import { GlobalLogger } from "../debugLogger";
import { useGlobalContext } from "../ScanViewerContext";
import { generateError } from "../../api/cardApiError";
import { State } from "xstate";
import { ScanViewContext, ScanViewEvent } from "../scanViewStateMachine";
import RequestingPermissionModal from "../../components/RequestingPermissionModal/RequestingPermissionModal";
import { isMobile } from "react-device-detect";
import { getBrowserName } from "../../utils/getInfoFunctions";
import { setupMockVideo } from "../../utils/mockVideoStream";

type ScanVideoContextType = {
  cameraPermissionState: CameraPermissionState;
  retryCamera: () => void;
};

const ScanVideoContext = createContext<ScanVideoContextType | null>(null);

export const useScanVideoContext = () => {
  const context = useContext(ScanVideoContext);
  if (!context) {
    throw new Error(
      "useScanVideoContext must be used within a ScanVideoProvider"
    );
  }
  return context;
};

const ScanVideoContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    setShowErrorModal,
    videoRef,
    onError,
    stateMachineSend,
    setVideoMetadata,
    stateMachineState,
    setScanDebugState,
    stateMachineService,
    cameraPermissionModalConfig,
    webToMobileHandoffConfig,
    live,
    internalOptions,
  } = useGlobalContext();
  const initialized = useRef(false);
  const srcObject = useRef() as MutableRefObject<MediaStream | null>;
  const [cameraPermissionState, setCameraPermissionState] =
    useState<CameraPermissionState>("idle");

  const showPermissionModalAfterDelay = useCallback(() => {
    const delayTime = internalOptions?.permissionModalDelay ?? 0;

    const timeoutId = setTimeout(() => {
      setCameraPermissionState((prev) => {
        if (prev === "idle") {
          return "requesting";
        }
        return prev;
      });
    }, delayTime * 1000);

    return () => clearTimeout(timeoutId);
  }, [internalOptions?.permissionModalDelay]);

  const getVideo = useCallback(
    async (videoBg: HTMLVideoElement) => {
      // Check if webcam mocking is enabled in sandbox mode
      GlobalLogger.debug(
        `Webcam mocking check: live=${live}, enableWebcamMocking=${internalOptions?.enableWebcamMocking}`,
        "normal"
      );
      if (!live && internalOptions?.enableWebcamMocking) {
        try {
          GlobalLogger.debug("Using mock webcam in sandbox mode", "normal");
          showPermissionModalAfterDelay();

          const { stream: mockStream, metadata } = await setupMockVideo(
            stateMachineState,
            internalOptions,
            internalOptions.mockWebcamSize
          );

          setShowErrorModal({ show: false, modal: "none" });
          setCameraPermissionState("granted");
          srcObject.current = mockStream;

          if (videoRef?.current) {
            videoRef.current.srcObject = mockStream;
          }

          setVideoMetadata(metadata);

          new Promise<MediaStream>((resolve) => {
            videoBg.onloadedmetadata = () => {
              resolve(mockStream);
            };
          });

          return;
        } catch (error: any) {
          GlobalLogger.error(`Mock video stream error: ${error.message}`);
          // Fall back to real camera if mock fails
        }
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        GlobalLogger.error(
          "Video Error - mediaDevices or getUserMedia undefined"
        );
        return;
      }

      if (
        cameraPermissionState === "hardblock" ||
        cameraPermissionState === "denied"
      ) {
        return;
      }

      const isFirefox = getBrowserName() === "Firefox";

      try {
        if (!isFirefox) {
          const permissionName: PermissionName = "camera" as PermissionName;
          const permissionStatus = await navigator.permissions.query({
            name: permissionName,
          });

          if (permissionStatus.state === "denied") {
            // Camera permission denied in settings
            const message = "Camera Error: Not Allowed";
            GlobalLogger.debug(message);
            const err = generateError(message, "VideoError", 677);
            stateMachineSend("CAMERAERROR");
            setCameraPermissionState("hardblock");
            if (onError) {
              onError(err);
            }
            if (cameraPermissionModalConfig?.enabled) {
              setShowErrorModal({
                show: true,
                modal:
                  webToMobileHandoffConfig?.enabled === true && !isMobile
                    ? "cameraPermission"
                    : "help",
              });
            } else if (
              webToMobileHandoffConfig?.enabled === true &&
              !isMobile
            ) {
              setShowErrorModal({
                show: true,
                modal: "webToMobile",
              });
            }
            return;
          }
        }
      } catch (error) {
        // Handle the error specifically for Firefox throwing an error with permissions.query
        GlobalLogger.error(`${error}`);
      }

      try {
        showPermissionModalAfterDelay();
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { min: 1280, ideal: 1920 },
            height: { min: 720, ideal: 1080 },
            facingMode: "environment",
          },
        });

        setShowErrorModal({ show: false, modal: "none" });
        setCameraPermissionState("granted");
        srcObject.current = stream;
        if (videoRef?.current) {
          videoRef.current.srcObject = stream;
        }
        const videoTrack = stream.getVideoTracks().pop();
        const settings = videoTrack?.getSettings() || {};
        const videoMetadata: { [key: string]: any } = {
          videoTrack: settings,
        };

        //only works on chrome powered
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const device = devices
            .filter((device) => device["kind"] === "videoinput")
            .pop();

          const currentDevice = devices.find(
            (device) => device.deviceId === settings.deviceId
          );

          if (currentDevice) {
            videoMetadata.cameraDeviceInfo = currentDevice;
          }

          // getCapabilities won't likely be supported by TS
          // https://github.com/microsoft/TypeScript/issues/45660
          if (device && device.getCapabilities != undefined) {
            const caps = device.getCapabilities();
            videoMetadata.cameraCapabilities = caps;
          }
        });

        setVideoMetadata(videoMetadata);

        // //1024x768 or less if video is smaller. If canvas is over 1024 we have GPU memory issues.
        // const captureCanvas = captureCanvasRef.current;
        // if (captureCanvas && settings.width && settings.height) {
        //   captureCanvas.width = Math.min(settings.width, 1024);
        //   captureCanvas.height = Math.min(settings.height, 768);
        // }

        new Promise<MediaStream>((resolve) => {
          videoBg.onloadedmetadata = () => {
            resolve(stream);
          };
        });
      } catch (error: any) {
        const err = generateError(error.message, "VideoError", 677);
        GlobalLogger.error(`Video Error: ${error.message} - 677`);
        if (onError) {
          onError(err);
        }
        //Only show error modal when error is a permissions error.
        let permissionState: CameraPermissionState = "idle";
        if (error.name === "NotAllowedError") {
          permissionState =
            err.message === "Permission dismissed" ? "denied" : "hardblock";
          stateMachineSend("CAMERAERROR");
          GlobalLogger.debug("Camera Error: Not Allowed");
        } else if (error.name === "NotFoundError") {
          permissionState = "notFoundDevice";
          stateMachineSend("CAMERAERROR");
          GlobalLogger.debug("Camera Error: Not Found");
        } else {
          permissionState = "denied";
          stateMachineSend("ERROR");
          GlobalLogger.debug("Camera Error: Unknown");
        }

        setCameraPermissionState(permissionState);
        const showCameraPermissionModal = cameraPermissionModalConfig?.enabled;
        const showWebToMobileModal =
          webToMobileHandoffConfig?.enabled === true && !isMobile;
        const isHardblockWithoutHandoff =
          permissionState === "hardblock" && !webToMobileHandoffConfig?.enabled;

        if (showCameraPermissionModal) {
          setShowErrorModal({
            show: true,
            modal:
              isHardblockWithoutHandoff || isMobile
                ? "help"
                : "cameraPermission",
          });
        } else if (showWebToMobileModal) {
          setShowErrorModal({ show: true, modal: "webToMobile" });
        }
      }
    },
    [
      onError,
      stateMachineSend,
      setScanDebugState,
      setShowErrorModal,
      setVideoMetadata,
      stateMachineState,
      cameraPermissionState,
      live,
      internalOptions,
      videoRef,
      showPermissionModalAfterDelay,
    ]
  );

  const loadVideo = () => {
    const videoBg = videoRef.current;
    if (videoBg) {
      videoBg.srcObject = srcObject.current;
      const videoPromise = videoBg.play();

      if (videoPromise === undefined) {
        GlobalLogger.error("Video Error - videoPromise undefined");
        initialized.current = false;
        return Promise.reject(new Error("Video promise is undefined"));
      }

      videoPromise
        .then(() => {
          stateMachineSend("CAMERA_LOADED");
          setScanDebugState("loading");
          GlobalLogger.debug("Camera Loaded");
          GlobalLogger.log("Video Started");
        })
        .catch((error: any) => {
          const err = generateError(error.message, "VideoError", 675);
          GlobalLogger.error(`Video Error: ${error.message} - 675`);
          initialized.current = false;

          if (onError) {
            onError(err);
          }
          stateMachineSend("ERROR");
        });
    }
  };

  const retryCamera = useCallback(async () => {
    try {
      if (srcObject.current) {
        srcObject.current.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        setShowErrorModal({ show: false, modal: "none" });
        stateMachineSend("retryCamera");
        setCameraPermissionState("retry");
      }
    } catch (error: any) {
      const err = generateError(error.message, "VideoError", 675);
      GlobalLogger.error(`Video Error: ${error.message} - 675`);
      initialized.current = false;
      if (onError) onError(err);
      stateMachineSend("ERROR");
    }
  }, [getVideo, onError, stateMachineSend, videoRef, cameraPermissionState]);

  useEffect(() => {
    if (cameraPermissionState === "retry" && videoRef.current) {
      getVideo(videoRef.current);
    }
  }, [cameraPermissionState]);

  useEffect(() => {
    const videoBg = videoRef.current;

    if (webToMobileHandoffConfig?.enabled === "always" && !isMobile) {
      stateMachineSend("CAMERAERROR");
      setShowErrorModal({
        show: true,
        modal: "webToMobile",
      });
      return;
    }

    if (videoBg && !initialized.current) {
      initialized.current = true;
      getVideo(videoBg);
    }
  }, [videoRef, getVideo, onError, initialized, stateMachineState.value]);

  useEffect(() => {
    const subscription = stateMachineService.subscribe(
      (state: State<ScanViewContext, ScanViewEvent>) => {
        if (state.matches("eligibilityProcessing")) {
          videoRef.current?.pause();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [stateMachineService]);

  useEffect(() => {
    if (stateMachineState.context.photoScanning && videoRef.current) {
      videoRef.current?.pause();
    } else if (
      !stateMachineState.context.photoScanning &&
      videoRef.current &&
      videoRef.current.paused &&
      stateMachineState.value !== "cameraLoading"
    ) {
      videoRef.current.play();
    }
  }, [videoRef, stateMachineState.context.photoScanning]);

  useEffect(() => {
    if (
      stateMachineState.context.modelState === "warmed" &&
      videoRef.current &&
      srcObject.current
    ) {
      loadVideo();

      return () => {
        srcObject.current?.getTracks().forEach((track) => {
          track.stop();
        });
      };
    }
  }, [stateMachineState.context.modelState, videoRef, srcObject.current]);

  return (
    <ScanVideoContext.Provider value={{ cameraPermissionState, retryCamera }}>
      <RequestingPermissionModal
        show={cameraPermissionState === "requesting"}
      />
      {children}
    </ScanVideoContext.Provider>
  );
};

export default ScanVideoContextProvider;
